/** The itinerary's months (plan §5 "Visibility & search": "12 compact toggles + 'any month' when
 *  none"): the months the catalog finds it in, as twelve 36 × 28 px cells in a row — a picked
 *  one navy-filled (a fill, not a hue: white on navy against ink on white) and "pressed" with
 *  the month's full name for a screen reader — and "Any month" after them when none is picked
 *  (the app's rule: no month is every month).
 *
 *  One geometry in both modes (the Zero-Re-Layout Rule): view mode draws the same cells, read
 *  only (`View`), so pressing E turns them into toggle buttons in place. The value is the month
 *  numbers 1–12 ascending, as the changeset sends them; an empty set is valid. */
import { alpha, Box, ButtonBase, Typography } from '@mui/material';

import type { RecordField, RecordWidget, RecordWidgetProps } from '@/features/records/editor/types';

import { monthsText } from './viewConfig';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** The words with no month picked — the app shows the itinerary in every month. */
export const ANY_MONTH = 'Any month';

/** The month numbers a value holds, 1–12, ascending (anything else dropped). */
export function monthSet(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((m): m is number => Number.isInteger(m) && m >= 1 && m <= 12))].sort((a, b) => a - b);
}

/** One month cell: 36 × 28, an 8 px radius; picked = navy fill, white label. */
const CELL_SX = {
  width: 36,
  height: 28,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  typography: 'caption',
  fontWeight: 600,
  color: 'text.secondary',
  bgcolor: 'background.paper',
  flex: 'none',
} as const;

const PICKED_SX = {
  bgcolor: 'primary.main',
  borderColor: 'primary.main',
  color: 'primary.contrastText',
} as const;

const ROW_SX = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', minHeight: 40, minWidth: 0 } as const;

function AnyMonth({ picked }: { picked: number[] }) {
  // The room is kept when months are picked: a pick never moves the row.
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      aria-hidden={picked.length > 0 || undefined}
      sx={{ ml: 1, visibility: picked.length > 0 ? 'hidden' : 'visible', whiteSpace: 'nowrap' }}
      data-any-month
    >
      {ANY_MONTH}
    </Typography>
  );
}

/** The months read-only — the same cells (view mode, or a field this operator may not edit). */
function MonthsView({ value }: { field: RecordField; value: unknown }) {
  const picked = monthSet(value);
  return (
    <Box sx={ROW_SX} role="img" aria-label={`Months: ${monthsText(picked)}`} data-months>
      {MONTHS.map((m, i) => (
        <Box key={m} aria-hidden sx={[CELL_SX, picked.includes(i + 1) && PICKED_SX]} data-month={i + 1} data-picked={picked.includes(i + 1) || undefined}>
          {m}
        </Box>
      ))}
      <AnyMonth picked={picked} />
    </Box>
  );
}

function MonthsInput(p: RecordWidgetProps) {
  const picked = monthSet(p.value);
  const toggle = (month: number) => {
    const next = picked.includes(month) ? picked.filter((m) => m !== month) : [...picked, month].sort((a, b) => a - b);
    p.onChange(next, { label: `${p.field.label}: ${monthsText(next)}` });
  };
  return (
    <Box sx={ROW_SX} role="group" aria-labelledby={p.labelId} aria-describedby={p.describedBy} id={p.id} data-months>
      {MONTHS.map((m, i) => {
        const month = i + 1;
        const on = picked.includes(month);
        return (
          <ButtonBase
            key={m}
            aria-pressed={on}
            aria-label={MONTH_NAMES[i]}
            disabled={p.disabled}
            onClick={() => toggle(month)}
            data-month={month}
            data-picked={on || undefined}
            sx={[
              CELL_SX,
              { '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) } },
              on && { ...PICKED_SX, '&:hover': { bgcolor: 'primary.dark' } },
              { '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 1 } },
            ]}
          >
            {m}
          </ButtonBase>
        );
      })}
      <AnyMonth picked={picked} />
    </Box>
  );
}

/** The `months` widget: its input, the same cells read-only, its words, and no "Required" —
 *  no month picked is "Any month". */
export const MonthsWidget: RecordWidget = Object.assign(MonthsInput, {
  View: MonthsView,
  formatChange: (value: unknown) => monthsText(monthSet(value)),
  checkValue: (value: unknown) =>
    value === null || (Array.isArray(value) && value.every((m) => Number.isInteger(m) && m >= 1 && m <= 12))
      ? null
      : 'Pick months from the list',
});
